<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1">
    <no-projects-alert v-if="noProjects"></no-projects-alert>
    <!-- the monthly limit of found messages of the plan: a warning from 90 %, red at the limit -->
    <v-alert v-if="usage && usage.level === 'full'" type="error" variant="tonal" class="mb-3">
      Лимит найденных сообщений по тарифу исчерпан ({{ formatCount(usage.used) }} из {{ formatCount(usage.limit) }}):
      мониторинг проектов остановлен до {{ resetDate(usage.resetsAt) }}. Чтобы возобновить его раньше, удалите ненужные
      найденные сообщения кнопкой «Удалить найденные» под фильтрами.
      <HelpHint section="plan" anchor="findings" text="Как считается лимит и как его освободить" />
    </v-alert>
    <v-alert v-else-if="usage && usage.level === 'warn'" type="warning" variant="tonal" closable class="mb-3">
      Осталось {{ formatCount(usage.limit - usage.used) }} из {{ formatCount(usage.limit) }} найденных сообщений до
      {{ resetDate(usage.resetsAt) }}. Когда лимит закончится, мониторинг проектов остановится; ненужные найденные сообщения
      можно удалить кнопкой «Удалить найденные» под фильтрами.
      <HelpHint section="plan" anchor="findings" text="Как считается лимит и как его освободить" />
    </v-alert>
    <!-- Header row 1: project chips -->
    <v-row align="center" class="mb-0 px-0">
      <v-col cols="12" class="d-flex align-center ga-2 flex-wrap">
        <!-- Project chips -->
        <v-chip
          v-for="project in projects"
          :key="project.id"
          :color="selectedProjectId === project.id ? 'primary' : 'default'"
          :variant="selectedProjectId === project.id ? 'elevated' : 'outlined'"
          @click="selectProject(project.id)"
          class="cursor-pointer"
        >
          {{ project.name }}
        </v-chip>
      </v-col>
    </v-row>

    <!-- Header row 2: view toggle (left) + count/filter (right) — always its own row -->
    <v-row align="center" class="mb-0 px-0" style="min-height: 48px;">
      <v-col cols="auto">
        <v-btn-toggle v-model="viewMode" mandatory density="compact" color="primary" variant="outlined">
          <v-btn value="flat" size="small">Список</v-btn>
          <v-btn value="stories" size="small">По сюжетам</v-btn>
        </v-btn-toggle>
        <HelpHint section="messages" anchor="stories" text="Что такое сюжеты и как сообщения в них собираются" class="ml-2" />
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <div class="d-flex align-center ga-2">
          <span v-if="viewMode === 'stories'" class="text-caption font-weight-bold">Сюжетов: {{ storiesTotal }}</span>
          <span v-else class="text-caption font-weight-bold">Найдено: {{ totalItems }}</span>
          <v-btn
            :icon="panelVisible ? 'mdi-filter-off' : 'mdi-filter'"
            variant="outlined"
            @click="panelVisible = !panelVisible"
            :color="panelVisible ? 'primary' : 'default'"
          />
        </div>
      </v-col>
    </v-row>

    <v-row>
      <!-- Main content column -->
      <v-col :cols="panelVisible ? 10 : 12" class="table-column-transition">
        <v-card>
          <v-card-text class="pa-0">
            <!-- Stories (grouped by event) view -->
            <div v-if="viewMode === 'stories'" class="messages-container pa-2">
              <MessageStoriesView
                :project-id="selectedProjectId"
                :filter-params="storyFilterParams"
                :highlight="filters.highlight"
                @open-message="openMessageModal"
                @total="storiesTotal = $event"
              />
            </div>

            <!-- Flat messages list -->
            <div
              v-show="viewMode === 'flat'"
              ref="messagesContainer"
              class="messages-container"
              @scroll="handleScroll"
            >
              <div v-if="isLoading && findings.length === 0" class="text-center pa-8">
                <v-progress-circular indeterminate color="primary" />
                <div class="mt-2">Загрузка сообщений...</div>
              </div>

              <div v-else-if="findings.length === 0" class="text-center pa-8">
                <v-icon size="64" color="grey-lighten-1">mdi-message-off</v-icon>
                <div class="mt-2 text-grey">Нет сообщений для отображения</div>
              </div>

              <template v-else>
                <div
                  v-for="finding in findings"
                  :key="finding._id"
                  class="message-card pa-4"
                  @click="openMessageModal(finding)"
                >
                  <!-- Message snippet -->
                  <div v-if="showOriginal && finding.content_original" class="message-content text-body-2 mb-2" style="white-space: pre-wrap;">{{ truncatePlain(finding.content_original, 200) }}<span v-if="finding.original_lang" class="text-caption text-medium-emphasis"> · оригинал ({{ finding.original_lang }})</span></div>
                  <div v-else class="message-content text-body-2 mb-2" v-html="formatMessageSnippet(filters.highlight ? (finding.highlighted_content || finding.content) : finding.content, filters.highlight ? finding.matched_queries : [], 200)">
                  </div>

                  <!-- Message footer -->
                  <div class="d-flex align-center justify-space-between">
                    <!-- Source info -->
                    <div class="d-flex align-center ga-2">
                      <SimpleIcon v-if="isTelegramPlatform(finding.platform_id)" name="telegram" :size="18" color="#9E9E9E" />
                      <v-icon v-else :icon="getPlatformIcon(finding.platform_id)" size="18" />
                      <a
                        v-if="isXPlatform(finding.platform_id)"
                        :href="finding.source_url"
                        target="_blank"
                        class="text-primary text-decoration-none text-caption"
                        @click.stop
                      >
                        {{ xAuthorLabel(finding) }}
                      </a>
                      <a
                        v-else
                        :href="finding.source_url"
                        target="_blank"
                        class="text-primary text-decoration-none text-caption"
                        @click.stop
                      >
                        {{ finding.source_name || 'Источник' }}
                      </a>
                      <span class="text-caption text-grey">
                        {{ formatDate(finding.published_at) }}
                      </span>
                      <!-- Edit detection badge -->
                      <v-chip
                        v-if="finding.is_edited"
                        :color="finding.still_matches === false ? 'warning' : 'info'"
                        size="x-small" variant="flat" label class="ml-1"
                      >
                        <v-icon start size="12">mdi-pencil</v-icon>
                        {{ finding.still_matches === false ? 'изменено — не соответствует' : 'изменено' }}
                        <v-tooltip activator="parent" location="top">
                          {{ finding.updated_at ? 'Обновлено ' + formatDate(finding.updated_at) : 'Контент изменился после сбора' }}
                        </v-tooltip>
                      </v-chip>
                    </div>

                    <!-- Action icons -->
                    <div class="d-flex align-center ga-1">
                      <!-- Link to original -->
                      <v-tooltip text="Открыть оригинал" location="top">
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-bind="props"
                            icon="mdi-open-in-new"
                            size="x-small"
                            variant="text"
                            :href="finding.source_url"
                            target="_blank"
                            @click.stop
                          />
                        </template>
                      </v-tooltip>

                      <!-- Sentiment icons -->
                      <v-tooltip :text="getEffectiveSentiment(finding) === 'negative' ? (isAiSentiment(finding) ? 'Негативный (AI)' : 'Негативный') : 'Негативный'" location="top">
                        <template v-slot:activator="{ props }">
                          <!-- TODO: restore @click.stop="setSentiment(finding, 'negative')" -->
                          <v-btn
                            v-bind="props"
                            :icon="getEffectiveSentiment(finding) === 'negative' ? 'mdi-emoticon-angry' : 'mdi-emoticon-angry-outline'"
                            size="x-small"
                            variant="text"
                            :color="getEffectiveSentiment(finding) === 'negative' ? 'error' : 'grey-lighten-1'"
                          />
                        </template>
                      </v-tooltip>
                      <v-tooltip :text="getEffectiveSentiment(finding) === 'neutral' ? (isAiSentiment(finding) ? 'Нейтральный (AI)' : 'Нейтральный') : 'Нейтральный'" location="top">
                        <template v-slot:activator="{ props }">
                          <!-- TODO: restore @click.stop="setSentiment(finding, 'neutral')" -->
                          <v-btn
                            v-bind="props"
                            :icon="getEffectiveSentiment(finding) === 'neutral' ? 'mdi-emoticon-neutral' : 'mdi-emoticon-neutral-outline'"
                            size="x-small"
                            variant="text"
                            :color="getEffectiveSentiment(finding) === 'neutral' ? 'warning' : 'grey-lighten-1'"
                          />
                        </template>
                      </v-tooltip>
                      <v-tooltip :text="getEffectiveSentiment(finding) === 'positive' ? (isAiSentiment(finding) ? 'Позитивный (AI)' : 'Позитивный') : 'Позитивный'" location="top">
                        <template v-slot:activator="{ props }">
                          <!-- TODO: restore @click.stop="setSentiment(finding, 'positive')" -->
                          <v-btn
                            v-bind="props"
                            :icon="getEffectiveSentiment(finding) === 'positive' ? 'mdi-emoticon-happy' : 'mdi-emoticon-happy-outline'"
                            size="x-small"
                            variant="text"
                            :color="getEffectiveSentiment(finding) === 'positive' ? 'success' : 'grey-lighten-1'"
                          />
                        </template>
                      </v-tooltip>

                      <!-- Manipulation icon -->
                      <v-menu
                        v-if="hasManipulation(finding)"
                        open-on-hover
                        location="top"
                        :close-on-content-click="false"
                      >
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-bind="props"
                            icon="mdi-alert-circle"
                            size="x-small"
                            variant="text"
                            color="error"
                            @click.stop
                          />
                        </template>
                        <v-card min-width="250" max-width="350" class="pa-2">
                          <v-card-title class="text-subtitle-2 pa-1">Манипуляции</v-card-title>
                          <v-card-text class="pa-1">
                            <v-chip
                              v-for="code in getManipulationTypes(finding)"
                              :key="code"
                              size="small"
                              color="error"
                              variant="outlined"
                              class="ma-1"
                            >
                              {{ getManipulationLabel(code) }}
                            </v-chip>
                          </v-card-text>
                        </v-card>
                      </v-menu>
                      <v-tooltip v-else text="Манипуляции не обнаружены" location="top">
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-bind="props"
                            icon="mdi-alert-circle-outline"
                            size="x-small"
                            variant="text"
                            color="grey-lighten-1"
                            @click.stop
                          />
                        </template>
                      </v-tooltip>

                      <!-- Cognitive bias icon -->
                      <v-menu
                        v-if="hasCognitiveBias(finding)"
                        open-on-hover
                        location="top"
                        :close-on-content-click="false"
                      >
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-bind="props"
                            icon="mdi-head-cog"
                            size="x-small"
                            variant="text"
                            color="error"
                            @click.stop
                          />
                        </template>
                        <v-card min-width="250" max-width="350" class="pa-2">
                          <v-card-title class="text-subtitle-2 pa-1">Когнитивные искажения</v-card-title>
                          <v-card-text class="pa-1">
                            <v-chip
                              v-for="code in getCognitiveBiasTypes(finding)"
                              :key="code"
                              size="small"
                              color="warning"
                              variant="outlined"
                              class="ma-1"
                            >
                              {{ getCognitiveBiasLabel(code) }}
                            </v-chip>
                          </v-card-text>
                        </v-card>
                      </v-menu>
                      <v-tooltip v-else text="Искажения не обнаружены" location="top">
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-bind="props"
                            icon="mdi-head-cog-outline"
                            size="x-small"
                            variant="text"
                            color="grey-lighten-1"
                            @click.stop
                          />
                        </template>
                      </v-tooltip>

                      <!-- the section «Анализ сообщений» of the help (the grey «?» opened a dictionary
                           dialog nobody found, 29.10.2026) -->
                      <HelpHint section="analysis" text="Тональность, манипуляции и искажения — как это считается" />
                    </div>
                  </div>
                </div>

                <!-- Loading indicator for infinite scroll -->
                <div v-if="isLoading" class="text-center pa-4">
                  <v-progress-circular indeterminate color="primary" size="24" />
                </div>

                <!-- No more data -->
                <div v-if="!hasMore && findings.length > 0" class="text-center pa-4 text-grey">
                  Все сообщения загружены
                </div>
              </template>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Filters panel -->
      <v-slide-x-reverse-transition>
        <v-col v-if="panelVisible" cols="2" key="filters">
          <v-card>
            <v-card-title class="text-subtitle-1">Фильтры</v-card-title>
            <v-card-text class="pa-3">
              <!-- Highlight toggle -->
              <v-switch
                v-model="filters.highlight"
                label="Подсвечивать найденное"
                color="primary"
                density="compact"
                hide-details
                class="mb-4"
                @update:model-value="onHighlightChange"
              />

              <!-- Stories view: hide stories of a single message (counted under the filters) -->
              <v-switch
                v-if="viewMode === 'stories'"
                v-model="multiStoriesOnly"
                label="Только сюжеты из нескольких сообщений"
                color="primary"
                density="compact"
                hide-details
                class="mb-4"
                @update:model-value="onMultiStoriesChange"
              />

              <!-- Language: показывать перевод или оригинал -->
              <div class="mb-4">
                <div class="text-caption text-medium-emphasis mb-1">
                  Язык
                  <HelpHint section="messages" anchor="translation" text="Иностранные сообщения переводятся; здесь можно показать оригинал" />
                </div>
                <v-btn-toggle v-model="showOriginal" density="compact" variant="outlined" divided mandatory>
                  <v-btn :value="false" size="small">Перевод</v-btn>
                  <v-btn :value="true" size="small">Оригинальный</v-btn>
                </v-btn-toggle>
              </div>

              <!-- Date range: From -->
              <v-text-field
                v-model="filters.publishedFrom"
                label="Дата публикации с"
                type="date"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-3"
                @update:model-value="onFilterChange"
              />

              <!-- Date range: To -->
              <v-text-field
                v-model="filters.publishedTo"
                label="Дата публикации по"
                type="date"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
                @update:model-value="onFilterChange"
              />

              <!-- Platform filter -->
              <v-select
                v-model="filters.platformId"
                :items="platformOptions"
                item-title="name"
                item-value="id"
                label="Платформа"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
                @update:model-value="onFilterChange"
              />

              <!-- Source autocomplete (for Telegram) -->
              <v-autocomplete
                v-model="filters.sourceId"
                :items="sourceOptions"
                :loading="sourceSearchLoading"
                item-title="display_name"
                item-value="id"
                label="Источник"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
                @update:search="searchSources"
                @update:model-value="onFilterChange"
                no-data-text="Введите минимум 3 символа"
              />

              <!-- Tags autocomplete -->
              <v-autocomplete
                v-model="filters.tags"
                :items="tagOptions"
                :loading="tagSearchLoading"
                item-title="name"
                item-value="id"
                label="Теги источников"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                multiple
                chips
                closable-chips
                class="mb-4"
                @update:search="searchTags"
                @update:model-value="onTagsChange"
                no-data-text="Введите минимум 2 символа"
                return-object
                ref="tagsAutocomplete"
              >
                <template #chip="{ props, item }">
                  <v-chip
                    v-bind="props"
                    :text="item.raw.name"
                    closable
                    size="small"
                    color="primary"
                  />
                </template>
              </v-autocomplete>

              <!-- Entity autocomplete -->
              <v-autocomplete
                v-model="filters.entity"
                :items="entityOptions"
                :loading="entitySearchLoading"
                item-title="name"
                item-value="id"
                label="Сущность"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
                @update:search="searchEntities"
                @update:model-value="onEntityChange"
                no-data-text="Введите минимум 2 символа"
                return-object
              >
                <template #item="{ props, item }">
                  <v-list-item v-bind="props" :subtitle="getEntityTypeName(item.raw.entity_type)" />
                </template>
              </v-autocomplete>

              <!-- Status filter (commented out - not relevant) -->
              <!--
              <v-select
                v-model="filters.status"
                :items="statusOptions"
                item-title="text"
                item-value="value"
                label="Статус"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
                @update:model-value="resetAndLoad"
              />
              -->

              <!-- Read filter (commented out - not relevant) -->
              <!--
              <v-select
                v-model="filters.isRead"
                :items="readOptions"
                item-title="text"
                item-value="value"
                label="Прочитано"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
                @update:model-value="resetAndLoad"
              />
              -->

              <!-- Reset filters -->
              <v-btn
                color="primary"
                variant="outlined"
                @click="resetFilters"
                prepend-icon="mdi-filter-remove"
                block
              >
                Сбросить
              </v-btn>

              <!-- Delete findings matching current filters -->
              <v-btn
                color="error"
                variant="outlined"
                class="mt-2"
                prepend-icon="mdi-delete-sweep"
                block
                :disabled="!totalItems"
                @click="deleteDialog = true"
              >
                Удалить найденные
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-slide-x-reverse-transition>
    </v-row>

    <!-- Confirm: delete findings by current filters -->
    <v-dialog v-model="deleteDialog" max-width="480" @keydown.esc="deleteDialog = false">
      <v-card>
        <v-card-title>Удаление сообщений</v-card-title>
        <v-card-text>
          <p>Будет удалено <strong>{{ totalItems }}</strong> сообщений проекта по текущим фильтрам.</p>
          <p class="text-caption text-medium-emphasis mb-0">
            Действие необратимо.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog = false" :disabled="deleting">Отмена</v-btn>
          <v-btn color="error" variant="elevated" :loading="deleting" @click="deleteFiltered">
            Удалить {{ totalItems }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Message detail modal -->
    <v-dialog
      v-model="messageModalVisible"
      max-width="800px"
      @keydown.esc="closeMessageModal"
    >
      <v-card v-if="selectedMessage">
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h6">Сообщение</span>
          <v-btn icon="mdi-close" variant="text" @click="closeMessageModal" />
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <!-- Source info: top link always → the original post, labelled "Оригинал" -->
          <div class="d-flex align-center ga-2 mb-4">
            <SimpleIcon v-if="isTelegramPlatform(selectedMessage.platform_id)" name="telegram" :size="24" color="#9E9E9E" />
            <v-icon v-else :icon="getPlatformIcon(selectedMessage.platform_id)" size="24" />
            <a
              v-if="selectedMessage.source_url"
              :href="selectedMessage.source_url"
              target="_blank"
              class="text-primary text-decoration-none"
            >
              Оригинал
            </a>
            <span class="text-grey">
              {{ formatDate(selectedMessage.published_at) }}
            </span>
            <v-chip
              v-if="selectedMessage.is_edited"
              :color="selectedMessage.still_matches === false ? 'warning' : 'info'"
              size="small" variant="flat" label class="ml-2"
            >
              <v-icon start size="14">mdi-pencil</v-icon>
              {{ selectedMessage.still_matches === false ? 'Изменено — больше не соответствует запросу' : 'Изменено' }}
              <span v-if="selectedMessage.updated_at" class="ml-1">· {{ formatDate(selectedMessage.updated_at) }}</span>
            </v-chip>
            <v-btn
              v-if="selectedMessage.is_edited"
              size="x-small" variant="text" color="primary" class="ml-1"
              :loading="editDiffLoading"
              @click="toggleEditDiff"
            >{{ editDiffOpen ? 'Скрыть изменения' : 'Показать изменения' }}</v-btn>
          </div>

          <!-- Inline diff of the last edit (old → new) -->
          <v-alert
            v-if="editDiffOpen"
            variant="tonal" color="info" density="compact" class="mb-3 edit-diff"
          >
            <div v-if="editDiffHtml" class="text-body-2" style="white-space: pre-wrap;" v-html="editDiffHtml"></div>
            <div v-else class="text-caption text-medium-emphasis">Предыдущая версия недоступна (правка поймана до включения истории).</div>
          </v-alert>

          <!-- Language toggle (only when this message was translated) -->
          <div v-if="selectedMessage.content_original" class="mb-3">
            <v-btn-toggle v-model="showOriginal" density="compact" variant="outlined" divided mandatory>
              <v-btn :value="false" size="small">Перевод</v-btn>
              <v-btn :value="true" size="small">Оригинал<span v-if="selectedMessage.original_lang" class="ml-1 text-caption">({{ selectedMessage.original_lang }})</span></v-btn>
            </v-btn-toggle>
          </div>

          <!-- Title (if present, e.g. Dzen articles) -->
          <div v-if="showOriginal && selectedMessage.title_original" class="text-h6 font-weight-bold mb-3">
            {{ selectedMessage.title_original }}
          </div>
          <!-- the found words are marked in the title too (data highlights it with the text) -->
          <div v-else-if="selectedMessage.title" class="message-title text-h6 font-weight-bold mb-3"
               v-html="formatMessageSnippet(filters.highlight ? (selectedMessage.highlighted_title || selectedMessage.title) : selectedMessage.title, [], 1000)">
          </div>

          <!-- Full content -->
          <div v-if="showOriginal && selectedMessage.content_original" class="message-full-content text-body-1 mb-4" style="white-space: pre-wrap;">{{ selectedMessage.content_original }}</div>
          <div v-else class="message-full-content text-body-1 mb-4" v-html="formatMessageText(filters.highlight ? (selectedMessage.highlighted_content || selectedMessage.content) : selectedMessage.content, filters.highlight ? selectedMessage.matched_queries : [], selectedMessage.platform_id)">
          </div>

          <!-- Author profile link: always the post AUTHOR's profile, anchor = author name -->
          <div v-if="authorProfileUrl(selectedMessage)" class="mb-1">
            <small class="text-medium-emphasis">Автор: </small>
            <a
              :href="authorProfileUrl(selectedMessage)"
              target="_blank"
              rel="noopener"
              class="text-primary text-decoration-none"
            >{{ authorDisplay(selectedMessage) }}</a>
          </div>

          <!-- Optional: X keyword-search origin (source_name is the query, not the author) -->
          <div v-if="xSearchKeyword(selectedMessage)" class="mb-3">
            <small class="text-medium-emphasis">Найдено по ключевому слову: </small>
            <a
              :href="xSearchUrl(xSearchKeyword(selectedMessage))"
              target="_blank"
              rel="noopener"
              class="text-primary text-decoration-none"
            >{{ xSearchKeyword(selectedMessage) }}</a>
          </div>

          <!-- Action icons (sentiment etc.) -->
          <div class="d-flex align-center ga-2">
            <!-- TODO: restore @click="setSentiment(selectedMessage, 'negative')" and remove style -->
            <v-btn
              :icon="getEffectiveSentiment(selectedMessage) === 'negative' ? 'mdi-emoticon-angry' : 'mdi-emoticon-angry-outline'"
              variant="outlined"
              size="small"
              :color="getEffectiveSentiment(selectedMessage) === 'negative' ? 'error' : 'default'"
              style="cursor: default; pointer-events: none"
            />
            <!-- TODO: restore @click="setSentiment(selectedMessage, 'neutral')" and remove style -->
            <v-btn
              :icon="getEffectiveSentiment(selectedMessage) === 'neutral' ? 'mdi-emoticon-neutral' : 'mdi-emoticon-neutral-outline'"
              variant="outlined"
              size="small"
              :color="getEffectiveSentiment(selectedMessage) === 'neutral' ? 'warning' : 'default'"
              style="cursor: default; pointer-events: none"
            />
            <!-- TODO: restore @click="setSentiment(selectedMessage, 'positive')" and remove style -->
            <v-btn
              :icon="getEffectiveSentiment(selectedMessage) === 'positive' ? 'mdi-emoticon-happy' : 'mdi-emoticon-happy-outline'"
              variant="outlined"
              size="small"
              :color="getEffectiveSentiment(selectedMessage) === 'positive' ? 'success' : 'default'"
              style="cursor: default; pointer-events: none"
            />

            <!-- Manipulation icon in modal -->
            <!-- TODO: remove class="non-clickable-btn" when restoring click functionality -->
            <v-menu
              v-if="hasManipulation(selectedMessage)"
              open-on-hover
              location="top"
              :close-on-content-click="false"
            >
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon="mdi-alert-circle"
                  variant="outlined"
                  size="small"
                  color="error"
                  class="non-clickable-btn"
                />
              </template>
              <v-card min-width="250" max-width="350" class="pa-2">
                <v-card-title class="text-subtitle-2 pa-1">Манипуляции</v-card-title>
                <v-card-text class="pa-1">
                  <v-chip
                    v-for="code in getManipulationTypes(selectedMessage)"
                    :key="code"
                    size="small"
                    color="error"
                    variant="outlined"
                    class="ma-1"
                  >
                    {{ getManipulationLabel(code) }}
                  </v-chip>
                </v-card-text>
              </v-card>
            </v-menu>
            <v-btn
              v-else
              icon="mdi-alert-circle-outline"
              variant="outlined"
              size="small"
              color="default"
              class="non-clickable-btn"
            />

            <!-- Cognitive bias icon in modal -->
            <!-- TODO: remove class="non-clickable-btn" when restoring click functionality -->
            <v-menu
              v-if="hasCognitiveBias(selectedMessage)"
              open-on-hover
              location="top"
              :close-on-content-click="false"
            >
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon="mdi-head-cog"
                  variant="outlined"
                  size="small"
                  color="error"
                  class="non-clickable-btn"
                />
              </template>
              <v-card min-width="250" max-width="350" class="pa-2">
                <v-card-title class="text-subtitle-2 pa-1">Когнитивные искажения</v-card-title>
                <v-card-text class="pa-1">
                  <v-chip
                    v-for="code in getCognitiveBiasTypes(selectedMessage)"
                    :key="code"
                    size="small"
                    color="warning"
                    variant="outlined"
                    class="ma-1"
                  >
                    {{ getCognitiveBiasLabel(code) }}
                  </v-chip>
                </v-card-text>
              </v-card>
            </v-menu>
            <v-btn
              v-else
              icon="mdi-head-cog-outline"
              variant="outlined"
              size="small"
              color="default"
              class="non-clickable-btn"
            />
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import { markersToText, QUOTE_HTML, dropLeftoverMarkers } from '@/utils/messageMarkers';
import NoProjectsAlert from "@/components/projects/NoProjectsAlert.vue";
import { loadPlanUsage, resetDate, formatCount } from "@/utils/planUsage";
import apiClient from "@/apiClient";
import apiDataClient from "@/apiDataClient";
import { stripInlineMarkup } from "@/utils/text";
import SimpleIcon from "@/components/SimpleIcon.vue";
import MessageStoriesView from "@/components/monitoring/MessageStoriesView.vue";

export default {
  name: "MonitoringMessages",
  components: {
    NoProjectsAlert,
    SimpleIcon,
    MessageStoriesView
  },
  emits: ["show-message"],
  data() {
    return {
      noProjects: false,
      usage: null,
      // Projects
      projects: [],
      selectedProjectId: null,

      // Findings
      findings: [],
      totalItems: 0,
      offset: 0,
      limit: 50,
      hasMore: true,
      isLoading: false,

      // UI state
      viewMode: 'flat',
      deleteDialog: false,
      deleting: false,   // 'flat' | 'stories'
      panelVisible: localStorage.getItem('messages-filters-panel') !== 'false',
      multiStoriesOnly: localStorage.getItem('messages-multi-stories') === 'true',
      storiesTotal: 0,
      messageModalVisible: false,
      selectedMessage: null,

      // Platforms mapping
      platforms: {},
      platformOptions: [],

      // Source search
      sourceOptions: [],
      sourceSearchLoading: false,

      // Язык отображения: false = перевод (по умолчанию), true = оригинал
      showOriginal: false,
      // Edit-detection «показать изменения» (inline diff) state for the card
      editDiffOpen: false,
      editDiffHtml: '',
      editDiffLoading: false,

      // Tags search
      tagOptions: [],
      tagSearchLoading: false,

      // Source IDs for tag filter (resolved from tags)
      tagSourceIds: null,

      // Entity search
      entityOptions: [],
      entitySearchLoading: false,

      // Filters
      filters: this.loadFiltersFromCookies(),

      // Filter options (commented filters kept for reference)
      statusOptions: [
        { text: 'Новые', value: 'new' },
        { text: 'Обработанные', value: 'enriched' },
        { text: 'В обработке', value: 'processing' },
        { text: 'С ошибкой', value: 'failed' },
      ],
      readOptions: [
        { text: 'Да', value: true },
        { text: 'Нет', value: false },
      ],

      // Auto-refresh
      autoRefreshInterval: null,
      refreshRate: 30, // default, will be loaded from settings
      isAtTop: true, // track if user is at top of the list

      // Manipulation type labels
      manipulationLabels: {
        'EMO': 'Эмоциональное разжигание',
        'CLK': 'Кликбейт',
        'FRM': 'Эффект формулировки',
        'CHR': 'Манипуляция фактами',
        'CSE': 'Ложная причинность',
        'DLM': 'Ложная дилемма',
        'STR': 'Соломенное чучело',
        'WBT': '«А как же...»',
        'ADH': 'Атака на личность',
        'PSR': 'Псевдоавторитеты',
        'FEQ': 'Ложное эквивалентирование',
        'FLA': 'Ложная аналогия',
        'FPR': 'Ложные обещания',
        'APU': 'Апелляция к большинству',
        'QMI': 'Вырывание из контекста',
        'FATTR': 'Ложное приписывание',
      },

      // Cognitive bias type labels
      cognitiveBiasLabels: {
        'CTS': 'Катастрофизация',
        'LAB': 'Навешивание ярлыков',
        'THR': 'Чтение мыслей',
        'NFCS': 'Фокус на негативе',
        'SHT': 'Долженствование',
        'COM': 'Обобщение',
        'EMD': 'Эмоц. рассуждение',
        'BW': 'Чёрно-белое мышление',
        'JWT': 'Каждый получает по заслугам',
        'PER': 'Персонализация',
        'FTL': 'Предсказание будущего',
        'CFL': 'Иллюзия контроля/беспомощности',
      },

      // Entity type labels
      entityTypeLabels: {
        'PER': 'Персона',
        'ORG': 'Организация',
        'LOC': 'Локация',
        'GPE': 'Геополитическая единица',
      },

    };
  },
  computed: {
    // Активные фильтры панели в формате query-параметров /findings.
    // Тот же набор, что строит loadFindings (без offset/limit/highlight) —
    // прокидывается в MessageStoriesView, чтобы вкладка «По сюжетам»
    // учитывала всю панель фильтров.
    storyFilterParams() {
      const params = {};
      if (this.filters.publishedFrom) {
        params.published_from = this.filters.publishedFrom + 'T00:00:00';
      }
      if (this.filters.publishedTo) {
        params.published_to = this.filters.publishedTo + 'T23:59:59';
      }
      if (this.filters.platformId) {
        params.platform_id = this.filters.platformId;
      }
      let sourceIds = [];
      if (this.filters.sourceId) {
        sourceIds.push(this.filters.sourceId);
      }
      if (this.tagSourceIds && this.tagSourceIds.length > 0) {
        if (sourceIds.length > 0) {
          sourceIds = sourceIds.filter(id => this.tagSourceIds.includes(id));
        } else {
          sourceIds = this.tagSourceIds;
        }
      }
      if (sourceIds.length > 0) {
        params.source_ids = sourceIds.join(',');
      }
      if (this.filters.status) {
        params.status = this.filters.status;
      }
      if (this.filters.isRead !== null) {
        params.is_read = this.filters.isRead;
      }
      if (this.filters.isArchived !== null) {
        params.is_archived = this.filters.isArchived;
      }
      if (this.filters.entity && this.filters.entity.id) {
        params.entity_id = this.filters.entity.id;
      }
      if (this.multiStoriesOnly) {
        params.min_findings = 2;
      }
      return params;
    },
  },
  methods: {
    resetDate,
    formatCount,
    async loadUsage() {
      try {
        this.usage = await loadPlanUsage();
      } catch (e) {
        this.usage = null;
      }
    },
    onMultiStoriesChange(value) {
      try {
        localStorage.setItem('messages-multi-stories', value ? 'true' : 'false');
      } catch (e) {
        // private window: the switch just is not remembered
      }
    },
    async loadProjects() {
      try {
        const response = await apiClient.get('/available-projects');
        this.projects = response.data;
        this.noProjects = !this.projects.length;

        // Try to restore saved project from cookie, or use first project
        if (this.projects.length > 0 && !this.selectedProjectId) {
          const savedProjectId = this.getSavedProjectId();
          // Check if saved project exists in available projects
          if (savedProjectId && this.projects.some(p => p.id === savedProjectId)) {
            this.selectedProjectId = savedProjectId;
          } else {
            this.selectedProjectId = this.projects[0].id;
          }
        }
      } catch (error) {
        console.error('Error loading projects:', error);
        this.$emit('show-message', 'Ошибка загрузки проектов', true);
      }
    },

    async loadPlatforms() {
      try {
        const response = await apiClient.get('/ref/platforms');
        // Convert to map for quick lookup and create options list
        this.platforms = {};
        this.platformOptions = [];
        for (const platform of response.data.items || response.data) {
          this.platforms[platform.id] = platform;
          this.platformOptions.push({ id: platform.id, name: this.platformDisplayName(platform.name) });
        }
      } catch (error) {
        console.error('Error loading platforms:', error);
      }
    },

    // Human-friendly platform label for the UI. The ref_platforms name is a
    // technical slug for some platforms (e.g. 'user_web'); map it for display
    // only — the platform_id used for filtering/matching is unchanged.
    platformDisplayName(name) {
      const map = {
        user_web: 'Сайты',
        google: 'Google',
        yandex: 'Яндекс',
        ahmia: 'Ahmia (Tor)',
        torch: 'Torch (Tor)',
        onionland: 'OnionLand (Tor)',
        tor66: 'Tor66 (Tor)',
        telegram: 'Telegram',
        dzen: 'Дзен',
        x: 'X.com',
        twitter: 'X.com',
      };
      return map[(name || '').toLowerCase()] || name;
    },

    async searchSources(query) {
      if (!query || query.length < 3) {
        this.sourceOptions = [];
        return;
      }
      this.sourceSearchLoading = true;
      try {
        // Use v2 API for platform sources
        const response = await apiClient.get('/v2/platform-sources', {
          params: { display_name: query, limit: 20 }
        });
        this.sourceOptions = (response.data.items || []).map(s => ({
          id: s.id,
          display_name: s.display_name || s.username
        }));
      } catch (error) {
        console.error('Error searching sources:', error);
        this.sourceOptions = [];
      } finally {
        this.sourceSearchLoading = false;
      }
    },

    async searchTags(query) {
      if (!query || query.length < 2) {
        this.tagOptions = [];
        return;
      }
      this.tagSearchLoading = true;
      try {
        // Use /tags endpoint (same as Telegram.vue)
        const response = await apiClient.get('/tags', {
          params: { q: query }
        });
        this.tagOptions = response.data.map(t => ({ id: t.id, name: t.name }));
      } catch (error) {
        console.error('Error searching tags:', error);
        this.tagOptions = [];
      } finally {
        this.tagSearchLoading = false;
      }
    },

    async searchEntities(query) {
      if (!query || query.length < 2 || !this.selectedProjectId) {
        this.entityOptions = [];
        return;
      }
      this.entitySearchLoading = true;
      try {
        const response = await apiClient.get(`/projects/${this.selectedProjectId}/entities`, {
          params: { search: query, limit: 20 }
        });
        this.entityOptions = (response.data.items || []).map(e => ({
          id: e.id,
          name: e.display_name || e.canonical_name,
          entity_type: e.entity_type
        }));
      } catch (error) {
        console.error('Error searching entities:', error);
        this.entityOptions = [];
      } finally {
        this.entitySearchLoading = false;
      }
    },

    onEntityChange() {
      // Clear query params if entity filter is being cleared
      if (!this.filters.entity && this.$route.query.entity_id) {
        this.$router.replace({ query: {} });
      }
      this.saveFiltersToCookies();
      this.resetAndLoad();
    },

    async onTagsChange(tags) {
      // Clear search field and refocus (like in Telegram.vue)
      this.$nextTick(() => {
        if (this.$refs.tagsAutocomplete) {
          try {
            this.$refs.tagsAutocomplete.search = '';
            this.$refs.tagsAutocomplete.focus();
          } catch (e) {
            // Ignore ref access errors
          }
        }
      });

      // When tags change, resolve them to source_ids
      // tags is now array of objects due to return-object
      if (!tags || tags.length === 0) {
        this.tagSourceIds = null;
      } else {
        try {
          // Extract IDs from tag objects
          const tagIds = tags.map(t => t.id);
          // Use v2 API for source-ids-by-tags
          const response = await apiClient.get('/v2/platform-sources/source-ids-by-tags', {
            params: { tags: tagIds.join(',') }
          });
          this.tagSourceIds = response.data.source_ids;
        } catch (error) {
          console.error('Error getting source_ids by tags:', error);
          this.tagSourceIds = null;
        }
      }
      this.saveFiltersToCookies();
      this.resetAndLoad();
    },

    onFilterChange() {
      this.saveFiltersToCookies();
      this.resetAndLoad();
    },

    loadFiltersFromCookies() {
      const getCookie = (name) => {
        const match = document.cookie.match(new RegExp(`${name}=([^;]*)`));
        return match ? match[1] : null;
      };

      return {
        highlight: getCookie('messages_highlight') !== 'false',
        publishedFrom: getCookie('messages_publishedFrom') || null,
        publishedTo: getCookie('messages_publishedTo') || null,
        platformId: getCookie('messages_platformId') ? parseInt(getCookie('messages_platformId')) : null,
        sourceId: getCookie('messages_sourceId') ? parseInt(getCookie('messages_sourceId')) : null,
        tags: getCookie('messages_tags') ? JSON.parse(decodeURIComponent(getCookie('messages_tags'))) : [],
        entity: getCookie('messages_entity') ? JSON.parse(decodeURIComponent(getCookie('messages_entity'))) : null,
        status: null,
        isRead: null,
        isArchived: false,
      };
    },

    saveFiltersToCookies() {
      const maxAge = 31536000; // 1 year
      document.cookie = `messages_highlight=${this.filters.highlight};path=/;max-age=${maxAge}`;

      if (this.filters.publishedFrom) {
        document.cookie = `messages_publishedFrom=${this.filters.publishedFrom};path=/;max-age=${maxAge}`;
      } else {
        document.cookie = `messages_publishedFrom=;path=/;max-age=0`;
      }

      if (this.filters.publishedTo) {
        document.cookie = `messages_publishedTo=${this.filters.publishedTo};path=/;max-age=${maxAge}`;
      } else {
        document.cookie = `messages_publishedTo=;path=/;max-age=0`;
      }

      if (this.filters.platformId) {
        document.cookie = `messages_platformId=${this.filters.platformId};path=/;max-age=${maxAge}`;
      } else {
        document.cookie = `messages_platformId=;path=/;max-age=0`;
      }

      if (this.filters.sourceId) {
        document.cookie = `messages_sourceId=${this.filters.sourceId};path=/;max-age=${maxAge}`;
      } else {
        document.cookie = `messages_sourceId=;path=/;max-age=0`;
      }

      if (this.filters.tags && this.filters.tags.length > 0) {
        document.cookie = `messages_tags=${encodeURIComponent(JSON.stringify(this.filters.tags))};path=/;max-age=${maxAge}`;
      } else {
        document.cookie = `messages_tags=;path=/;max-age=0`;
      }

      if (this.filters.entity) {
        document.cookie = `messages_entity=${encodeURIComponent(JSON.stringify(this.filters.entity))};path=/;max-age=${maxAge}`;
      } else {
        document.cookie = `messages_entity=;path=/;max-age=0`;
      }
    },

    async loadFindings(append = false) {
      if (!this.selectedProjectId) return;
      if (this.isLoading) return;

      this.isLoading = true;

      try {
        const params = {
          offset: append ? this.offset : 0,
          limit: this.limit,
          highlight: this.filters.highlight,
        };

        // Date range filter
        if (this.filters.publishedFrom) {
          params.published_from = this.filters.publishedFrom + 'T00:00:00';
        }
        if (this.filters.publishedTo) {
          params.published_to = this.filters.publishedTo + 'T23:59:59';
        }

        // Platform filter
        if (this.filters.platformId) {
          params.platform_id = this.filters.platformId;
        }

        // Source filter (single source or sources from tags)
        let sourceIds = [];
        if (this.filters.sourceId) {
          sourceIds.push(this.filters.sourceId);
        }
        if (this.tagSourceIds && this.tagSourceIds.length > 0) {
          // If both sourceId and tags are set, intersect them
          if (sourceIds.length > 0) {
            sourceIds = sourceIds.filter(id => this.tagSourceIds.includes(id));
          } else {
            sourceIds = this.tagSourceIds;
          }
        }
        if (sourceIds.length > 0) {
          params.source_ids = sourceIds.join(',');
        }

        // Legacy filters (commented out in UI)
        if (this.filters.status) {
          params.status = this.filters.status;
        }
        if (this.filters.isRead !== null) {
          params.is_read = this.filters.isRead;
        }
        if (this.filters.isArchived !== null) {
          params.is_archived = this.filters.isArchived;
        }

        // Entity filter - pass entity_id, Data Service will fetch finding_ids
        if (this.filters.entity && this.filters.entity.id) {
          params.entity_id = this.filters.entity.id;
        }

        const response = await apiDataClient.get(
          `/projects/${this.selectedProjectId}/findings`,
          { params }
        );

        const newFindings = response.data.items || [];
        this.totalItems = response.data.total;

        if (append) {
          this.findings = [...this.findings, ...newFindings];
        } else {
          this.findings = newFindings;
        }

        this.offset = this.findings.length;
        this.hasMore = this.findings.length < this.totalItems;

      } catch (error) {
        console.error('Error loading findings:', error);
        this.$emit('show-message', 'Ошибка загрузки сообщений', true);
      } finally {
        this.isLoading = false;
      }
    },

    selectProject(projectId) {
      if (this.selectedProjectId === projectId) return;
      this.selectedProjectId = projectId;
      // Save to cookie
      document.cookie = `messages_selected_project=${projectId};path=/;max-age=31536000`;
      // Clear entity filter since entities are project-specific
      if (this.filters.entity) {
        this.filters.entity = null;
        this.entityOptions = [];
        // Clear entity cookie
        document.cookie = `messages_entity=;path=/;max-age=0`;
        // Clear query params if entity filter was from navigation
        if (this.$route.query.entity_id) {
          this.$router.replace({ query: {} });
        }
      }
      this.resetAndLoad();
    },

    getSavedProjectId() {
      const match = document.cookie.match(/messages_selected_project=(\d+)/);
      return match ? parseInt(match[1], 10) : null;
    },

    onHighlightChange(value) {
      // Save to cookie
      document.cookie = `messages_highlight=${value};path=/;max-age=31536000`;
      // Reload findings with new highlight setting
      this.resetAndLoad();
    },

    resetAndLoad() {
      this.findings = [];
      this.offset = 0;
      this.hasMore = true;
      this.loadFindings();
    },

    async deleteFiltered() {
      // Удаление findings по ТЕМ ЖЕ фильтрам, что и выдача (см. storyFilterParams).
      this.deleting = true;
      try {
        const params = { ...this.storyFilterParams };
        const r = await apiDataClient.delete(
          `/projects/${this.selectedProjectId}/findings/by-filter`,
          { params }
        );
        this.$emit('show-message', `Удалено сообщений: ${r.data.deleted}`);
        this.deleteDialog = false;
        this.resetAndLoad();
      } catch (e) {
        console.error('deleteFiltered error:', e);
        this.$emit('show-message', 'Не удалось удалить сообщения', true);
      } finally {
        this.deleting = false;
        this.loadUsage(); // deleting frees the monthly limit
      }
    },

    resetFilters() {
      // Keep highlight setting, reset other filters
      this.filters = {
        highlight: this.filters.highlight,
        publishedFrom: null,
        publishedTo: null,
        platformId: null,
        sourceId: null,
        tags: [],
        entity: null,
        status: null,
        isRead: null,
        isArchived: false,
      };
      this.tagSourceIds = null;
      this.sourceOptions = [];
      this.tagOptions = [];
      this.entityOptions = [];
      // Clear query params if entity filter was from navigation
      if (this.$route.query.entity_id) {
        this.$router.replace({ query: {} });
      }
      this.saveFiltersToCookies();
      this.resetAndLoad();
    },

    handleScroll(event) {
      const container = event.target;
      const scrollTop = container.scrollTop;
      const scrollBottom = container.scrollHeight - scrollTop - container.clientHeight;

      // Track if user is at top (within 50px threshold)
      this.isAtTop = scrollTop < 50;

      // Load more when near bottom
      if (scrollBottom < 200 && this.hasMore && !this.isLoading) {
        this.loadFindings(true);
      }
    },

    openMessageModal(finding) {
      this.selectedMessage = finding;
      this.messageModalVisible = true;
      // reset edit-diff state for the newly opened message
      this.editDiffOpen = false;
      this.editDiffHtml = '';
      this.editDiffLoading = false;
    },

    closeMessageModal() {
      this.messageModalVisible = false;
      this.selectedMessage = null;
      this.editDiffOpen = false;
      this.editDiffHtml = '';
    },

    // Toggle + lazily load the «before→after» inline diff for an edited message.
    async toggleEditDiff() {
      if (this.editDiffOpen) { this.editDiffOpen = false; return; }
      this.editDiffOpen = true;
      if (this.editDiffHtml) return;  // already loaded
      const cid = this.selectedMessage && this.selectedMessage.content_item_id;
      if (!cid) { this.editDiffHtml = ''; return; }
      this.editDiffLoading = true;
      try {
        const { data } = await apiDataClient.get(`/source-content/${encodeURIComponent(cid)}/revisions`);
        const revs = data.revisions || [];
        if (!revs.length) { this.editDiffHtml = ''; return; }
        // Most recent previous version → current.
        this.editDiffHtml = this.buildWordDiff(revs[0].content || '', data.content || '');
      } catch (e) {
        this.editDiffHtml = '';
      } finally {
        this.editDiffLoading = false;
      }
    },

    // Word-level inline diff → HTML with <del>/<ins>. Strips HTML tags first
    // (content may carry link markup) and escapes, so the output is injection-safe.
    buildWordDiff(oldRaw, newRaw) {
      const strip = (s) => (s || '').replace(/<[^>]+>/g, '');
      const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const a = strip(oldRaw).split(/(\s+)/);
      const b = strip(newRaw).split(/(\s+)/);
      // LCS table (word tokens).
      const n = a.length, m = b.length;
      const dp = Array.from({ length: n + 1 }, () => new Int32Array(m + 1));
      for (let i = n - 1; i >= 0; i--)
        for (let j = m - 1; j >= 0; j--)
          dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
      let i = 0, j = 0, out = '';
      while (i < n && j < m) {
        if (a[i] === b[j]) { out += esc(a[i]); i++; j++; }
        else if (dp[i + 1][j] >= dp[i][j + 1]) { out += `<del class="diff-del">${esc(a[i])}</del>`; i++; }
        else { out += `<ins class="diff-ins">${esc(b[j])}</ins>`; j++; }
      }
      while (i < n) { out += `<del class="diff-del">${esc(a[i])}</del>`; i++; }
      while (j < m) { out += `<ins class="diff-ins">${esc(b[j])}</ins>`; j++; }
      return out;
    },

    truncateContent(content, maxLength) {
      if (!content) return '';
      if (content.length <= maxLength) return content;
      return content.substring(0, maxLength) + '...';
    },
    truncatePlain(content, maxLength) {
      return this.truncateContent(content, maxLength);
    },

    formatMessageText(text, matchedQueries = [], platformId = null) {
      if (!text) return '';

      let formattedText = text;

      // Подсветка внутри адреса ссылки ломает href — там она не нужна
      formattedText = formattedText.replace(/\{\{LINK:([^|]*)\|/g, (match, url) => {
        return '{{LINK:' + url.replace(/<\/?em>/g, '') + '|';
      });

      // Извлекаем ES highlights (<em>...</em>) ПЕРЕД экранированием HTML.
      // Метка без фигурных скобок: иначе её "}}" закрывает {{LINK:..|текст}},
      // если подсвеченное слово стоит в тексте ссылки.
      const esHighlights = [];
      const esMarkerRegex = /\uE000(\d+)\uE001/g;
      formattedText = formattedText.replace(/<em>([^<]+)<\/em>/g, (match, content) => {
        const marker = `\uE000${esHighlights.length}\uE001`;
        esHighlights.push(content);
        return marker;
      });
      const esHighlightHtml = (index) => {
        const safeContent = esHighlights[parseInt(index)]
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        return `<mark class="search-highlight">${safeContent}</mark>`;
      };

      // Экранируем HTML символы
      formattedText = formattedText
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

      // Обрабатываем старые маркеры из базы данных (для обратной совместимости)
      formattedText = formattedText.replace(/___LINE_BREAK___/g, '\n');
      formattedText = formattedText.replace(/___MENTION_\d+___/g, '');
      formattedText = formattedText.replace(/_LINE_BREAK_/g, '\n');
      formattedText = formattedText.replace(/__MENTION_\d+__MENTION_/g, '');

      // Сохраняем переносы строк до начала обработки
      const lineBreaks = [];
      let lineBreakIndex = 0;
      formattedText = formattedText.replace(/\n/g, () => {
        const marker = `{{LINEBREAK${lineBreakIndex}}}`;
        lineBreaks.push(marker);
        lineBreakIndex++;
        return marker;
      });

      // Используем временные маркеры для защиты от повторной обработки
      const mentionMarker = '{{MENTION}}';
      const linkMarker = '{{LINKMARKER}}';
      const highlightMarker = '{{HIGHLIGHT}}';
      const processedMentions = [];
      const processedLinks = [];
      const processedHighlights = [];

      // 1. Обрабатываем маркеры ссылок {{LINK:url|text}}
      formattedText = formattedText.replace(
        /\{\{LINK:([^|]+)\|([^}]+)\}\}/g,
        (match, url, linkText) => {
          let linkClass = 'message-link';
          if (url.includes('t.me/')) {
            linkClass = 'channel-link';
          } else if (url.startsWith('http')) {
            linkClass = 'external-link';
          }

          // Текст ссылки уходит из formattedText — подсветку в нём восстанавливаем сразу
          const linkTextHtml = linkText.replace(esMarkerRegex, (m, index) => esHighlightHtml(index));
          const linkHtml = `<a href="${url}" target="_blank" class="${linkClass}">${linkTextHtml}</a>`;
          processedLinks.push(linkHtml);
          return linkMarker + (processedLinks.length - 1) + linkMarker;
        }
      );

      // 2. Обрабатываем упоминания @username (исключаем email адреса)
      formattedText = formattedText.replace(
        /(?<![a-zA-Z0-9._])@([a-zA-Z0-9_]{1,32})(?![a-zA-Z0-9._])/g,
        (match, username) => {
          const href = this.mentionUrlFor(platformId, username);
          const linkHtml = `<a href="${href}" target="_blank" class="mention-link">@${username}</a>`;
          processedMentions.push(linkHtml);
          return mentionMarker + (processedMentions.length - 1) + mentionMarker;
        }
      );

      // 3. Восстанавливаем ES highlights как <mark> (если есть)
      formattedText = formattedText.replace(esMarkerRegex, (match, index) => {
        processedHighlights.push(esHighlightHtml(index));
        return highlightMarker + (processedHighlights.length - 1) + highlightMarker;
      });

      // 4. Если нет ES highlights, подсвечиваем слова из поисковых запросов (fallback)
      if (esHighlights.length === 0 && matchedQueries && matchedQueries.length > 0) {
        // Извлекаем все слова из запросов (разбиваем по пробелам, исключаем операторы)
        const searchWords = [];
        for (const query of matchedQueries) {
          const words = query.split(/\s+/).filter(word => {
            // Исключаем операторы и короткие слова
            const lowerWord = word.toLowerCase();
            return word.length >= 2 &&
                   !['или', 'and', 'not', 'or', 'и'].includes(lowerWord) &&
                   !word.startsWith('-');
          });
          searchWords.push(...words);
        }

        // Подсвечиваем точные совпадения слов
        if (searchWords.length > 0) {
          const uniqueWords = [...new Set(searchWords)];
          for (const word of uniqueWords) {
            // Убираем кавычки если есть
            const cleanWord = word.replace(/['"]/g, '');
            if (cleanWord.length < 2) continue;

            // Точное совпадение слова (с границами для кириллицы и латиницы)
            // Используем lookbehind и lookahead для границ слов
            const wordRegex = new RegExp(`(?<![а-яёa-z0-9])(${this.escapeRegExp(cleanWord)})(?![а-яёa-z0-9])`, 'gi');
            formattedText = formattedText.replace(wordRegex, (match, p1) => {
              const highlightHtml = `<mark class="search-highlight">${p1}</mark>`;
              processedHighlights.push(highlightHtml);
              return highlightMarker + (processedHighlights.length - 1) + highlightMarker;
            });
          }
        }
      }

      // 4. Обрабатываем код `text`
      formattedText = formattedText.replace(/`([^`]+)`/g, '<code>$1</code>');

      // 5. Обрабатываем подчеркивание __text__
      formattedText = formattedText.replace(/__([^_]+)__/g, '<u>$1</u>');

      // 6. Обрабатываем жирный текст **text**
      formattedText = formattedText.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');

      // 7. Обрабатываем курсив *text*
      formattedText = formattedText.replace(/\*([^*]+)\*/g, '<i>$1</i>');

      // 8. Восстанавливаем обработанные ссылки
      formattedText = formattedText.replace(new RegExp(linkMarker + '(\\d+)' + linkMarker, 'g'), (match, index) => {
        return processedLinks[parseInt(index)];
      });

      // 9. Восстанавливаем обработанные упоминания
      formattedText = formattedText.replace(new RegExp(mentionMarker + '(\\d+)' + mentionMarker, 'g'), (match, index) => {
        return processedMentions[parseInt(index)];
      });

      // 10. Восстанавливаем подсветку поисковых слов
      formattedText = formattedText.replace(new RegExp(highlightMarker + '(\\d+)' + highlightMarker, 'g'), (match, index) => {
        return processedHighlights[parseInt(index)];
      });

      // 11. Восстанавливаем переносы строк
      lineBreaks.forEach((marker) => {
        formattedText = formattedText.replace(marker, '<br>');
      });

      // 12. Обрабатываем блок-цитаты {{BLOCKQUOTE:text}} (в том числе многострочные)
      formattedText = formattedText.replace(QUOTE_HTML, '<blockquote class="message-blockquote">$1</blockquote>');

      return dropLeftoverMarkers(formattedText);
    },

    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },

    formatMessageSnippet(text, matchedQueries = [], maxLength = 200) {
      if (!text) return '';

      // Очищаем служебные маркеры, оставляя только текст внутри них
      let cleanText = text;

      // Все служебные маркеры (ссылки, цитаты, в том числе пустые и вложенные, старые) — общий список
      cleanText = markersToText(cleanText);
      // Пометки форматирования (**жирный**) в превью не нужны
      cleanText = stripInlineMarkup(cleanText);

      // Убираем переносы строк, заменяем на пробелы
      cleanText = cleanText.replace(/\n+/g, ' ');

      // Убираем множественные пробелы
      cleanText = cleanText.replace(/\s+/g, ' ').trim();

      // Делим на обычный текст и ES highlights (<em>...</em>): нечётные части — подсветка.
      // Обрезаем по видимой длине и только целыми частями подсветки, иначе обрез
      // попадает в середину метки и она остаётся в тексте.
      const escapeHtml = (value) => value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      const parts = cleanText.split(/<em>([^<]+)<\/em>/g);
      const highlights = parts.filter((part, index) => index % 2 === 1);
      let visibleLength = 0;
      let truncated = false;
      cleanText = '';
      for (let index = 0; index < parts.length; index++) {
        const part = parts[index];
        const room = maxLength - visibleLength;
        if (index % 2 === 1) {
          if (part.length > room) {
            truncated = true;
            break;
          }
          cleanText += `<mark class="search-highlight">${escapeHtml(part)}</mark>`;
          visibleLength += part.length;
        } else if (part.length > room) {
          cleanText += escapeHtml(part.substring(0, room));
          truncated = true;
          break;
        } else {
          cleanText += escapeHtml(part);
          visibleLength += part.length;
        }
      }
      if (truncated) {
        cleanText = cleanText.trim() + '...';
      }

      // Если нет ES highlights, подсвечиваем слова из поисковых запросов (fallback)
      if (highlights.length === 0 && matchedQueries && matchedQueries.length > 0) {
        const searchWords = [];
        for (const query of matchedQueries) {
          const words = query.split(/\s+/).filter(word => {
            const lowerWord = word.toLowerCase();
            return word.length >= 2 &&
                   !['или', 'and', 'not', 'or', 'и'].includes(lowerWord) &&
                   !word.startsWith('-');
          });
          searchWords.push(...words);
        }

        if (searchWords.length > 0) {
          const uniqueWords = [...new Set(searchWords)];
          for (const word of uniqueWords) {
            const cleanWord = word.replace(/['"]/g, '');
            if (cleanWord.length < 2) continue;

            const wordRegex = new RegExp(`(?<![а-яёa-z0-9])(${this.escapeRegExp(cleanWord)})(?![а-яёa-z0-9])`, 'gi');
            cleanText = cleanText.replace(wordRegex, '<mark class="search-highlight">$1</mark>');
          }
        }
      }

      return cleanText;
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    pluralize(count, one, few, many) {
      // Russian pluralization: 1 сообщение, 2-4 сообщения, 5-20 сообщений
      const n = Math.abs(count);
      const lastTwo = n % 100;
      const lastOne = n % 10;

      if (lastTwo >= 11 && lastTwo <= 19) return many;
      if (lastOne === 1) return one;
      if (lastOne >= 2 && lastOne <= 4) return few;
      return many;
    },

    getPlatformIcon(platformId) {
      const platform = this.platforms[platformId];
      return platform?.icon || 'mdi-web';
    },

    isTelegramPlatform(platformId) {
      const platform = this.platforms[platformId];
      return platform?.icon === 'mdi-telegram' || platform?.name?.toLowerCase() === 'telegram';
    },

    isXPlatform(platformId) {
      // ref_platforms.id=13 is X.com; name fallback covers future renames.
      if (platformId === 13) return true;
      const name = (this.platforms[platformId]?.name || '').toLowerCase();
      return name.includes('x.com') || name === 'twitter' || name === 'x';
    },

    xAuthorLabel(finding) {
      // Preferred: denormalised author_name (e.g. 'Elon Musk'). Fallback:
      // @handle parsed from source_url. Last resort: source_name.
      if (finding?.author_name) return finding.author_name;
      const handle = this.authorHandleOf(finding);
      if (handle) return `@${handle}`;
      return finding?.source_name || 'X.com';
    },

    // Human-readable author for the modal (anchor text of both the top
    // 'go to original' link and the bottom 'Автор:' line).
    readableAuthor(msg) {
      if (!msg) return 'Источник';
      // For X / Dzen we denormalised author_name / publisher_name into
      // the finding; prefer it.
      if (msg.author_name) return msg.author_name;
      // For Telegram and legacy findings, source_name holds the
      // channel / publisher label.
      if (msg.source_name) return msg.source_name;
      const handle = this.authorHandleOf(msg);
      if (handle) return `@${handle}`;
      return 'Источник';
    },

    // URL that opens the author's profile/channel page on the source
    // platform. Prefers explicit denormalised fields, falls back to the
    // platform-specific template.
    authorProfileUrl(msg) {
      if (!msg) return '';
      // Dzen keeps the channel URL already on the finding.
      if (msg.publisher_url) return msg.publisher_url;
      const handle = this.authorHandleOf(msg);
      if (!handle) return '';
      return this.mentionUrlFor(msg.platform_id, handle);
    },

    // Platform-aware URL templates: tweaked per-platform so @mentions and
    // author links go to the right site. Match by platform_id first
    // (canonical), name as a fallback.
    platformName(platformId) {
      return (this.platforms[platformId]?.name || '').toLowerCase();
    },
    mentionUrlFor(platformId, username) {
      // Known platform_id → site mapping (ref_platforms in core DB)
      const byId = {
        13: `https://x.com/${username}`,        // X.com
        12: `https://dzen.ru/${username}`,      // Дзен
        10: `https://youtube.com/${username}`,  // Youtube
        8:  `https://vk.com/${username}`,       // Вконтакте
        7:  `https://t.me/${username}`,         // Telegram
      };
      if (byId[platformId]) return byId[platformId];
      // Name-based fallback for resilience.
      const name = this.platformName(platformId);
      if (name.includes('x.com') || name === 'twitter' || name === 'x') return `https://x.com/${username}`;
      if (name.includes('дзен') || name === 'dzen') return `https://dzen.ru/${username}`;
      if (name.includes('youtube')) return `https://youtube.com/${username}`;
      if (name.includes('вконтакте') || name === 'vk') return `https://vk.com/${username}`;
      return `https://t.me/${username}`;
    },
    authorHandleOf(msg) {
      if (!msg) return '';
      // Dzen stores the channel under platform_data.publisher_url instead
      // of a flat handle field — special-case it first.
      const pub = msg.platform_data?.publisher_url || '';
      if (pub) {
        const m = pub.match(/^https?:\/\/(?:www\.)?dzen\.ru\/([^/?#]+)(?:\/|$)/i);
        if (m && m[1] !== 'a') return m[1];
      }
      const direct = (msg.platform_data?.author_username
        || msg.author_username
        || msg.telegram_username
        || '').replace(/^@/, '');
      if (direct) return direct;
      // Fallback: parse the handle out of source_url. Supported formats:
      //   https://x.com/{handle}/status/{id}
      //   https://twitter.com/{handle}/status/{id}
      //   https://t.me/{handle}/{id}
      //   https://dzen.ru/{handle}
      //   https://vk.com/{handle}
      const url = msg.source_url || '';
      let m = url.match(/^https?:\/\/(?:www\.)?(?:x|twitter)\.com\/([^/?#]+)(?:\/|$)/i);
      if (m) return m[1];
      m = url.match(/^https?:\/\/(?:www\.)?t\.me\/([^/?#]+)(?:\/|$)/i);
      if (m && m[1] !== 's') return m[1];
      m = url.match(/^https?:\/\/(?:www\.)?dzen\.ru\/([^/?#]+)(?:\/|$)/i);
      // dzen.ru/a/{id} is an article URL, not a channel handle. Skip it
      // — the actual publisher lives in platform_data.publisher_url which
      // we read above and is the only reliable source for Dzen.
      if (m && m[1] !== 'a') return m[1];
      m = url.match(/^https?:\/\/(?:www\.)?vk\.com\/([^/?#]+)(?:\/|$)/i);
      if (m) return m[1];
      return '';
    },
    authorNameOf(msg) {
      return msg?.platform_data?.author_name || msg?.author_name || this.authorHandleOf(msg);
    },

    // Anchor text for the bottom "Автор:" line — the human author of THIS post.
    // For X the source_name may be a search query, so we never fall back to it
    // there; for Telegram/Dzen source_name is the channel/publisher label.
    authorDisplay(msg) {
      if (!msg) return 'Источник';
      if (msg.author_name) return msg.author_name;
      const handle = this.authorHandleOf(msg);
      if (this.isXPlatform(msg.platform_id)) {
        return handle ? `@${handle}` : 'Автор';
      }
      if (msg.source_name) return msg.source_name;
      return handle ? `@${handle}` : 'Источник';
    },

    // If this X finding came from a keyword-search source, source_name holds
    // the query (e.g. 'СВО') — distinct from the tweet author. For a profile
    // source the tweets are from that profile, so source_name == the author.
    // Returns the query string, or '' when it's not a search origin.
    xSearchKeyword(msg) {
      if (!msg || !this.isXPlatform(msg.platform_id)) return '';
      const q = (msg.source_name || '').trim();
      if (!q) return '';
      const name = (msg.author_name || '').trim();
      const handle = this.authorHandleOf(msg);
      if (q === name || q === handle || q === `@${handle}`) return '';
      return q;
    },
    xSearchUrl(query) {
      return `https://x.com/search?q=${encodeURIComponent(query)}&src=typed_query`;
    },

    setSentiment(finding, sentiment) {
      // Toggle sentiment
      if (finding.user_sentiment === sentiment) {
        finding.user_sentiment = null;
      } else {
        finding.user_sentiment = sentiment;
      }
      // TODO: Save to backend
    },

    /**
     * Get effective sentiment for a finding.
     * Priority: user_sentiment (manual) > sentiment.label (AI-generated)
     */
    getEffectiveSentiment(finding) {
      // User sentiment (manual) has priority
      if (finding.user_sentiment) {
        return finding.user_sentiment;
      }
      // Fallback to AI-generated sentiment
      if (finding.sentiment && finding.sentiment.label) {
        return finding.sentiment.label;
      }
      return null;
    },

    /**
     * Check if sentiment is from AI (not user-set)
     */
    isAiSentiment(finding) {
      return !finding.user_sentiment && finding.sentiment && finding.sentiment.label;
    },

    hasManipulation(finding) {
      return finding.manipulations && finding.manipulations.detected &&
             finding.manipulations.types && finding.manipulations.types.length > 0;
    },

    hasCognitiveBias(finding) {
      return finding.cognitive_biases && finding.cognitive_biases.detected &&
             finding.cognitive_biases.types && finding.cognitive_biases.types.length > 0;
    },

    getManipulationTypes(finding) {
      if (!finding.manipulations || !finding.manipulations.types) {
        return [];
      }
      return finding.manipulations.types;
    },

    getCognitiveBiasTypes(finding) {
      if (!finding.cognitive_biases || !finding.cognitive_biases.types) {
        return [];
      }
      return finding.cognitive_biases.types;
    },

    getManipulationLabel(code) {
      return this.manipulationLabels[code] || code;
    },

    getCognitiveBiasLabel(code) {
      return this.cognitiveBiasLabels[code] || code;
    },

    getEntityTypeName(type) {
      return this.entityTypeLabels[type] || type;
    },

    async loadRefreshSettings() {
      // /admin/settings доступен только админу — у пользователя это давало
      // 403-шум в консоли; не-админ просто живёт на дефолтном интервале.
      if (!this.$store.getters.isAdmin) return;
      try {
        const response = await apiClient.get('/admin/settings', {
          params: { name: 'base' }
        });
        if (response.data && response.data.frontend_refresh_rate) {
          this.refreshRate = response.data.frontend_refresh_rate;
        }
      } catch (error) {
        console.error('Error loading refresh settings:', error);
        // Use default 30 seconds
      }
    },

    startAutoRefresh() {
      // Clear existing interval if any
      this.stopAutoRefresh();

      if (this.refreshRate > 0) {
        this.autoRefreshInterval = setInterval(() => {
          // Only refresh if not loading, no modal is open, user is at top, and on first page
          if (!this.isLoading && !this.messageModalVisible && this.selectedProjectId && this.isAtTop && this.offset <= this.limit) {
            // Refresh without resetting scroll - only get new items at offset 0
            this.refreshFindings();
          }
        }, this.refreshRate * 1000);
      }
    },

    stopAutoRefresh() {
      if (this.autoRefreshInterval) {
        clearInterval(this.autoRefreshInterval);
        this.autoRefreshInterval = null;
      }
    },

    async refreshFindings() {
      // Refresh findings without losing scroll position
      // Just reload current visible items
      if (!this.selectedProjectId) return;
      if (this.isLoading) return;

      this.isLoading = true;

      try {
        const params = {
          offset: 0,
          limit: Math.max(this.findings.length, this.limit),
          highlight: this.filters.highlight,
        };

        // Date range filter
        if (this.filters.publishedFrom) {
          params.published_from = this.filters.publishedFrom + 'T00:00:00';
        }
        if (this.filters.publishedTo) {
          params.published_to = this.filters.publishedTo + 'T23:59:59';
        }

        // Platform filter
        if (this.filters.platformId) {
          params.platform_id = this.filters.platformId;
        }

        // Source filter
        let sourceIds = [];
        if (this.filters.sourceId) {
          sourceIds.push(this.filters.sourceId);
        }
        if (this.tagSourceIds && this.tagSourceIds.length > 0) {
          if (sourceIds.length > 0) {
            sourceIds = sourceIds.filter(id => this.tagSourceIds.includes(id));
          } else {
            sourceIds = this.tagSourceIds;
          }
        }
        if (sourceIds.length > 0) {
          params.source_ids = sourceIds.join(',');
        }

        // Legacy filters
        if (this.filters.status) {
          params.status = this.filters.status;
        }
        if (this.filters.isRead !== null) {
          params.is_read = this.filters.isRead;
        }
        if (this.filters.isArchived !== null) {
          params.is_archived = this.filters.isArchived;
        }

        // Entity filter
        if (this.filters.entity && this.filters.entity.id) {
          params.entity_id = this.filters.entity.id;
        }

        const response = await apiDataClient.get(
          `/projects/${this.selectedProjectId}/findings`,
          { params }
        );

        const newFindings = response.data.items || [];
        this.totalItems = response.data.total;
        this.findings = newFindings;
        this.offset = this.findings.length;
        this.hasMore = this.findings.length < this.totalItems;

      } catch (error) {
        console.error('Error refreshing findings:', error);
      } finally {
        this.isLoading = false;
      }
    },
  },
  watch: {
    panelVisible(newValue) {
      localStorage.setItem('messages-filters-panel', newValue);
    }
  },
  async mounted() {
    this.loadUsage();
    await Promise.all([
      this.loadProjects(),
      this.loadPlatforms(),
      this.loadRefreshSettings(),
    ]);

    // Check for entity filter from query params (coming from Entities/Dashboard)
    const entityId = this.$route.query.entity_id;
    const entityName = this.$route.query.entity_name;
    const queryProjectId = this.$route.query.project_id;
    const queryPublishedFrom = this.$route.query.published_from;
    const queryPublishedTo = this.$route.query.published_to;

    if (entityId && queryProjectId) {
      // Set project from query if provided
      this.selectedProjectId = parseInt(queryProjectId);

      // Create entity object for the filter
      const entityObj = {
        id: parseInt(entityId),
        name: entityName || 'Сущность'
      };

      // Clear existing filters when opening with entity filter, set entity
      this.filters = {
        highlight: this.filters.highlight,
        publishedFrom: queryPublishedFrom || null,
        publishedTo: queryPublishedTo || null,
        platformId: null,
        sourceId: null,
        tags: [],
        entity: entityObj,
        status: null,
        isRead: null,
        isArchived: false,
      };
      this.tagSourceIds = null;
      this.sourceOptions = [];
      this.tagOptions = [];
      // Populate entityOptions so autocomplete displays the selected entity
      this.entityOptions = [entityObj];

      // Open filters panel so user sees the entity filter
      this.panelVisible = true;

      // Don't clear query params - keep them for page refresh
    }

    // Restore sourceId if loaded from cookies - fetch source info for display
    if (this.filters.sourceId) {
      try {
        const response = await apiClient.get(`/v2/platform-sources/${this.filters.sourceId}`);
        if (response.data) {
          this.sourceOptions = [{
            id: response.data.id,
            display_name: response.data.display_name || response.data.username
          }];
        }
      } catch (error) {
        console.error('Error restoring source info:', error);
        // If source not found, clear the filter
        this.filters.sourceId = null;
      }
    }

    // Restore tagSourceIds if tags were loaded from cookies
    if (this.filters.tags && this.filters.tags.length > 0) {
      try {
        const tagIds = this.filters.tags.map(t => t.id);
        const response = await apiClient.get('/v2/platform-sources/source-ids-by-tags', {
          params: { tags: tagIds.join(',') }
        });
        this.tagSourceIds = response.data.source_ids;
        // Also populate tagOptions for displaying selected tags
        this.tagOptions = [...this.filters.tags];
      } catch (error) {
        console.error('Error restoring tagSourceIds:', error);
      }
    }

    // Restore entity if loaded from cookies
    if (this.filters.entity && this.filters.entity.id) {
      // Populate entityOptions for displaying selected entity
      this.entityOptions = [this.filters.entity];
    }

    if (this.selectedProjectId) {
      this.loadFindings();
    }

    // Start auto-refresh
    this.startAutoRefresh();
  },
  beforeUnmount() {
    // Stop auto-refresh
    this.stopAutoRefresh();
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

/* Inline edit diff (v-html content → :deep to pierce scoped styles) */
.edit-diff :deep(del.diff-del) {
  background: rgba(244, 67, 54, 0.15);
  color: #c62828;
  text-decoration: line-through;
}
.edit-diff :deep(ins.diff-ins) {
  background: rgba(76, 175, 80, 0.18);
  color: #2e7d32;
  text-decoration: none;
}

.table-column-transition {
  transition: flex-basis 0.3s ease, max-width 0.3s ease;
}

.messages-container {
  height: calc(100vh - 236px);
  overflow-y: auto;
}

.message-card {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  cursor: pointer;
  transition: background-color 0.2s;
}

.message-card:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.message-content {
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.5;
}

.message-full-content {
  max-height: 60vh;
  overflow-y: auto;
}

/* Стили для ссылок в тексте сообщений */
.message-content :deep(a),
.message-full-content :deep(a) {
  text-decoration: none;
  transition: all 0.2s ease;
}

.message-content :deep(a:hover),
.message-full-content :deep(a:hover) {
  text-decoration: underline;
}

/* Обычные ссылки в тексте */
.message-content :deep(.message-link),
.message-full-content :deep(.message-link) {
  color: #0066cc;
  font-weight: 500;
}

.message-content :deep(.message-link:hover),
.message-full-content :deep(.message-link:hover) {
  color: #0052cc;
}

/* Упоминания @username */
.message-content :deep(.mention-link),
.message-full-content :deep(.mention-link) {
  color: #1da1f2;
  font-weight: 500;
}

.message-content :deep(.mention-link:hover),
.message-full-content :deep(.mention-link:hover) {
  color: #0d8bd9;
}

/* Ссылки на каналы */
.message-content :deep(.channel-link),
.message-full-content :deep(.channel-link) {
  color: #0088cc;
}

.message-content :deep(.channel-link:hover),
.message-full-content :deep(.channel-link:hover) {
  color: #006699;
}

/* Внешние ссылки */
.message-content :deep(.external-link),
.message-full-content :deep(.external-link) {
  color: #0066cc;
}

.message-content :deep(.external-link:hover),
.message-full-content :deep(.external-link:hover) {
  color: #0052cc;
}

/* Стили для code */
.message-content :deep(code),
.message-full-content :deep(code) {
  background-color: rgba(0, 0, 0, 0.05);
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-family: monospace;
  font-size: 0.875em;
}

/* Стили для blockquote */
.message-content :deep(blockquote),
.message-content :deep(.message-blockquote),
.message-full-content :deep(blockquote),
.message-full-content :deep(.message-blockquote) {
  margin: 0.5rem 0;
  padding: 0.5rem 0.75rem;
  border-left: 3px solid #1976d2;
  background-color: rgba(0, 0, 0, 0.03);
  font-style: italic;
  color: rgba(0, 0, 0, 0.7);
}

/* Подсветка поисковых запросов */
.message-content :deep(.search-highlight),
.message-full-content :deep(.search-highlight),
.message-title :deep(.search-highlight) {
  background-color: #ffeb3b;
  color: #000;
  padding: 0.1rem 0.2rem;
  border-radius: 2px;
  font-weight: 500;
}

/* Non-clickable button style - only changes cursor, preserves hover events */
.non-clickable-btn {
  cursor: default !important;
}
</style>
